import { useParams } from 'react-router-dom';
import { Padding } from 'Components/Padding';
import Breadcrumb from 'Components/Breadcrumb/Breadcrumb';
import CertificateDownload from 'Components/CertificateDownload/CertificateDownload';
import CourseStatusChip from 'Components/CourseStatusChip';
import GenericTableText from 'Components/GenericTableText/GenericTableText';
import SideBarContainer from 'Containers/SideBarContainer';
import { useHistoryCourseDetailPage } from 'Hooks/useHistoryCourseDetailPage';
import ReportsLayout from 'Pages/reports/components/ReportsLayout/ReportsLayout';
import ReportsTitle from 'Pages/reports/components/ReportsTitle/ReportsTitle';
import { formatDate } from 'Utils/format-date';
import { HistoryCourseEmployeesFilter } from './components/HistoryCourseEmployeesFilter/HistoryCourseEmployeesFilter';
import ServerPaginatedTable from './components/ServerPaginatedTable/ServerPaginatedTable';
import TotalCourseEmployeesDetails from './components/TotalCourseEmployeesDetails/TotalCourseEmployeesDetails';
import { FaPeopleGroup } from 'react-icons/fa6';
import { MdSchool } from 'react-icons/md';

interface Params {
  courseId: string;
}

interface RowData {
  id: string;
  userId: string;
  payrollNum: number;
  name: string;
  terminal: string;
  department: string;
  jobTitle: string;
  gender: string;
  age: number | string;
  createdAt: string;
  finishedAt: string | React.ReactNode;
  statusId: string;
  statusName: string;
  certificationId: string | null;
  downloadCertificate: React.ReactNode;
  [key: string]: unknown;
}

export default function Page() {
  const { courseId } = useParams<Params>();

  const {
    t2,
    data,
    meta,
    totals,
    loading,
    error,
    course,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleExport,
    handleDownloadCertificate,
    handleRowClick,
  } = useHistoryCourseDetailPage(courseId);

  const rows: RowData[] = data.map(employee => ({
    id: employee.id,
    userId: employee.userId,
    payrollNum: employee.payrollNum,
    name: employee.name,
    terminal: employee.terminal?.name || '-',
    department: employee.department?.name || '-',
    jobTitle: employee.jobTitle?.name || '-',
    gender: employee.gender || '-',
    age: employee.age ?? '-',
    createdAt: employee.createdAt ? formatDate(employee.createdAt) : '-',
    finishedAt: employee.finishedAt ? formatDate(employee.finishedAt) : <GenericTableText text={t2('not_finished')} />,
    statusId: employee.status?.id || '',
    statusName: employee.status?.name || '-',
    certificationId: employee.certificationId,
    downloadCertificate: (
      <CertificateDownload
        certificationId={employee.certificationId}
        onDownload={() => handleDownloadCertificate(employee.id, employee.certificationId ?? '')}
      />
    ),
  }));

  const buttons = [
    {
      icon: <FaPeopleGroup size={24} />,
      tooltip: t2('employees_report_tooltip'),
      route: '/reporte/historial/empleados',
    },
    { icon: <MdSchool size={24} />, tooltip: t2('courses_report_tooltip'), route: '/reporte/historial/cursos' },
  ];

  return (
    <SideBarContainer>
      <Padding padding="1rem 1rem 0 1rem">
        <Breadcrumb
          items={[
            { label: t2('history_reports'), path: '/reporte/historial/cursos' },
            { label: t2('course_detail'), path: `/reporte/historial/cursos/${courseId}` },
          ]}
        />
      </Padding>
      <ReportsLayout
        title={<ReportsTitle title={t2('title')} accent={`"${course?.name || ''}"`} />}
        buttons={buttons}
        onExport={handleExport}
      />
      <Padding padding="1rem">
        <TotalCourseEmployeesDetails totals={totals} />
        <HistoryCourseEmployeesFilter onSearch={handleSearch} />
        <Padding padding="0.5rem 0" />
        <ServerPaginatedTable<RowData>
          columns={[
            { id: 'payrollNum', label: t2('payroll_number'), align: 'center' },
            { id: 'name', label: t2('employee_name') },
            { id: 'terminal', label: t2('terminal') },
            { id: 'department', label: t2('department') },
            { id: 'jobTitle', label: t2('job_title') },
            { id: 'gender', label: t2('gender') },
            { id: 'age', label: t2('age'), align: 'center' },
            { id: 'createdAt', label: t2('start_date') },
            { id: 'finishedAt', label: t2('finished_date') },
            { id: 'statusName', label: t2('status'), align: 'center' },
            { id: 'downloadCertificate', label: t2('certificate'), align: 'center' },
          ]}
          rows={rows}
          meta={meta}
          loading={loading}
          error={error}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onRowClick={handleRowClick}
          getRowContent={(row, columnId) => {
            if (columnId === 'statusName') {
              return <CourseStatusChip statusId={row.statusId} status={row.statusName} />;
            }
            return row[columnId] as React.ReactNode;
          }}
          noDataMessage={t2('no_data')}
        />
      </Padding>
    </SideBarContainer>
  );
}
